import { decodeJwt } from 'jose'

// How far the clock can be trusted right now:
// - 'pending': a measurement has not finished yet, or one is in flight.
// - 'trusted': an offset was applied, so now() is on the server timeline.
// - 'untrusted': measuring finished without an offset (slow round trip,
//   unreadable token, samples that disagree), so now() is the raw device clock.
export type ServerClockStatus = 'pending' | 'trusted' | 'untrusted'

// The device clock corrected onto the server clock. Elapsed-time math that
// subtracts a serverTimestamp() value (e.g. the slide timer) must use now()
// instead of Date.now() so a wrong device clock does not skew the result.
export interface ServerClock {
  now(): number
  readonly status: ServerClockStatus
  // Resolves once the status is no longer 'pending', or after timeoutMs,
  // whichever comes first. For one-shot decisions that should not run on a
  // clock that is about to be corrected.
  whenSettled(timeoutMs: number): Promise<void>
  // Called by the auth flow with the timing of a forced ID token fetch.
  recordFromIdToken(
    token: string,
    sentAtMs: number,
    receivedAtMs: number,
    refetchToken?: () => Promise<string>
  ): Promise<void>
}

// How long callers wait for a pending server clock before going ahead on the
// clock as it stands, so nothing hangs if a measurement never completes.
export const SERVER_CLOCK_WAIT_MS = 5_000

type LogEvent = (name: string, params: Record<string, unknown>) => void

// The result of a single client↔server clock-offset measurement: a usable
// reading ('ok'), a round trip too slow to trust ('slow'), or a token we could
// not read an `iat` from ('unusable').
type ClockOffsetSample =
  | { kind: 'ok'; offsetMs: number; rttMs: number }
  | { kind: 'slow'; rttMs: number }
  | { kind: 'unusable' }

// The token exchange with Google's secure token service is a sub-second request.
// A round trip much larger than that means the midpoint is a poor proxy for
// "server time now" (asymmetric-latency error grows with the round trip), so we
// discard such a sample rather than commit a low-quality offset.
const MAX_CLOCK_OFFSET_RTT_MS = 10_000

// An offset this large is more likely a stale token than a real clock skew, so
// we don't trust a single sample of it — see recordFromIdToken.
const MAX_TRUSTED_CLOCK_OFFSET_MS = 15 * 60 * 1000

// Two independent samples must agree within this window to corroborate a large
// offset. Generous enough to absorb `iat`'s one-second granularity and RTT jitter.
const CLOCK_OFFSET_AGREEMENT_MS = 5_000

// Compute a single client↔server offset sample from an ID token and the timing of
// the request that fetched it. The token's `iat` (issued-at) claim is server time
// in seconds at the moment Google's auth servers signed it, so comparing it to the
// local clock at the round-trip midpoint yields the offset (NTP-style). Never
// throws: a missing `iat` or unparseable token is reported as 'unusable'.
function measureClockOffset(
  token: string,
  sentAtMs: number,
  receivedAtMs: number
): ClockOffsetSample {
  try {
    const { iat } = decodeJwt(token)
    if (!iat) return { kind: 'unusable' }

    const rttMs = receivedAtMs - sentAtMs
    if (rttMs > MAX_CLOCK_OFFSET_RTT_MS) {
      return { kind: 'slow', rttMs }
    }

    const offsetMs = iat * 1000 - (sentAtMs + receivedAtMs) / 2
    return { kind: 'ok', offsetMs, rttMs }
  } catch {
    return { kind: 'unusable' }
  }
}

// A ServerClock whose offset comes from Firebase ID tokens. The auth flow
// already force-fetches a token before starting Firestore streams, so the offset
// piggybacks on that request instead of paying for a dedicated one.
export class IdTokenServerClock implements ServerClock {
  // serverClock - deviceClock, in ms. 0 until an offset is applied, so now()
  // behaves exactly like Date.now() until (and unless) it is measured.
  private offsetMs = 0
  // True only while a measurement is in flight, which for a large offset spans
  // a second token round trip.
  private measuring = false
  // True once a measurement has finished, whatever its outcome.
  private measured = false
  // True once an offset has been applied.
  private applied = false

  // Callbacks waiting in whenSettled() for the current measurement to finish.
  private readonly settledListeners = new Set<() => void>()

  private readonly logEvent: LogEvent

  constructor(logEvent: LogEvent) {
    this.logEvent = logEvent
  }

  now() {
    return Date.now() + this.offsetMs
  }

  get status(): ServerClockStatus {
    if (this.measuring || !this.measured) return 'pending'
    return this.applied ? 'trusted' : 'untrusted'
  }

  whenSettled(timeoutMs: number): Promise<void> {
    if (this.status !== 'pending') return Promise.resolve()
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer)
        this.settledListeners.delete(done)
        resolve()
      }
      const timer = setTimeout(done, timeoutMs)
      this.settledListeners.add(done)
    })
  }

  // A small offset is trusted immediately (the common case). A large offset is
  // ambiguous — it could be a genuinely wrong device clock (which we *want* to
  // correct) or a stale token (which we must reject), and one sample can't tell
  // them apart. So we take a second independent sample via `refetchToken` and
  // apply the offset only if the two corroborate: a real skew reproduces, a stale
  // token collapses to ~0. On any failure the offset is left unchanged.
  async recordFromIdToken(
    token: string,
    sentAtMs: number,
    receivedAtMs: number,
    refetchToken?: () => Promise<string>
  ) {
    const apply = (offsetMs: number, rttMs: number) => {
      this.offsetMs = offsetMs
      this.applied = true
      this.logEvent('slide_timer_clock_offset', {
        offset_ms: Math.round(offsetMs),
        abs_offset_ms: Math.round(Math.abs(offsetMs)),
        rtt_ms: Math.round(rttMs),
      })
    }
    const reject = (offsetMs: number, reason: string) => {
      this.logEvent('slide_timer_clock_offset', {
        offset_ms: Math.round(offsetMs),
        abs_offset_ms: Math.round(Math.abs(offsetMs)),
        rejected: true,
        reason,
      })
    }

    // Mark the measurement in flight until it finishes. This is synchronous for
    // every fast path (no await before their returns), so only the large-offset
    // corroboration — which awaits a second token round trip — actually holds
    // the status at 'pending'. The finally guarantees it is always cleared.
    this.measuring = true
    try {
      const first = measureClockOffset(token, sentAtMs, receivedAtMs)
      if (first.kind === 'unusable') {
        this.logEvent('slide_timer_clock_offset', {
          rejected: true,
          reason: 'unusable',
        })
        return
      }
      if (first.kind === 'slow') {
        // Round trip too slow to trust the midpoint: leave the prior/default
        // offset in place. Still logged so the field can size how often we skip.
        this.logEvent('slide_timer_clock_offset', {
          rtt_ms: Math.round(first.rttMs),
          rejected: true,
          reason: 'rtt',
        })
        return
      }

      if (Math.abs(first.offsetMs) <= MAX_TRUSTED_CLOCK_OFFSET_MS) {
        apply(first.offsetMs, first.rttMs)
        return
      }

      // Large offset: corroborate with a second independent sample before
      // trusting it. Without a way to re-fetch we can't corroborate, so we reject.
      if (!refetchToken) {
        reject(first.offsetMs, 'uncorroborated')
        return
      }

      let second: ClockOffsetSample
      try {
        const retrySentAt = Date.now()
        const retryToken = await refetchToken()
        second = measureClockOffset(retryToken, retrySentAt, Date.now())
      } catch {
        second = { kind: 'unusable' }
      }

      if (second.kind !== 'ok') {
        reject(first.offsetMs, 'uncorroborated')
        return
      }

      if (
        Math.abs(first.offsetMs - second.offsetMs) > CLOCK_OFFSET_AGREEMENT_MS
      ) {
        // The two samples disagree — the large first reading was a glitch/stale
        // token, not a real skew. Keep the prior/default offset.
        reject(second.offsetMs, 'inconsistent')
        return
      }

      // Corroborated: a genuinely skewed device clock. Use the second (more
      // recent) sample.
      apply(second.offsetMs, second.rttMs)
    } finally {
      this.measuring = false
      this.measured = true
      for (const listener of [...this.settledListeners]) listener()
    }
  }
}
